import React, { useState, useEffect } from 'react';

export default function CredentialsManager({ onLocationsUpdated }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    restaurant_id: '',
    bearer_token: '',
    location_code: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  async function fetchLocations() {
    try {
      setLoading(true);
      const response = await fetch('/api/credentials');
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setMessage('Failed to load locations');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(location) {
    setEditingId(location.id);
    setFormData({
      name: location.name,
      restaurant_id: location.restaurant_id,
      bearer_token: location.bearer_token,
      location_code: location.location_code || ''
    });
  }

  function handleCancel() {
    setEditingId(null);
    setFormData({ name: '', restaurant_id: '', bearer_token: '', location_code: '' });
  }

  async function handleSave() {
    try {
      if (!formData.name || !formData.restaurant_id || !formData.bearer_token) {
        setMessage('All fields are required');
        setMessageType('error');
        return;
      }

      const url = editingId
        ? `/api/credentials/${editingId}`
        : '/api/credentials';

      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          restaurant_id: formData.restaurant_id,
          bearer_token: formData.bearer_token,
          location_code: formData.location_code
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.error || 'Failed to save');
        setMessageType('error');
      } else {
        setMessage(data.message || 'Saved successfully!');
        setMessageType('success');
        handleCancel();
        fetchLocations();
        onLocationsUpdated();
      }
    } catch (error) {
      setMessage(error.message || 'Error saving');
      setMessageType('error');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      const response = await fetch(`/api/credentials/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.error || 'Failed to delete');
        setMessageType('error');
      } else {
        setMessage('Location deleted');
        setMessageType('success');
        fetchLocations();
        onLocationsUpdated();
      }
    } catch (error) {
      setMessage(error.message || 'Error deleting');
      setMessageType('error');
    }
  }

  return (
    <div className="credentials-manager">
      <h2>🏪 Manage Locations</h2>
      <p className="subtitle">Save and manage credentials for multiple Toast locations</p>

      {message && (
        <div className={`message ${messageType}`}>
          {messageType === 'success' ? '✓' : '✕'} {message}
        </div>
      )}

      <div className="card">
        <h3>{editingId ? '✏️ Edit Location' : '➕ Add New Location'}</h3>

        <div className="form-group">
          <label>Location Name (e.g., "Downtown", "Main St")</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Friendly name for this location"
          />
        </div>

        <div className="form-group">
          <label>Location Code (3 letters, e.g. "NYC") — prefixes ProductClass names</label>
          <input
            type="text"
            maxLength={3}
            value={formData.location_code}
            onChange={(e) => setFormData({ ...formData, location_code: e.target.value.toUpperCase() })}
            placeholder="e.g., NYC"
          />
        </div>

        <div className="form-group">
          <label>Toast Restaurant External ID</label>
          <input
            type="text"
            value={formData.restaurant_id}
            onChange={(e) => setFormData({ ...formData, restaurant_id: e.target.value })}
            placeholder="e.g., 9f7e57e9-cbae-4403-a552-248395953acc"
          />
        </div>

        <div className="form-group">
          <label>Bearer Token</label>
          <input
            type="password"
            value={formData.bearer_token}
            onChange={(e) => setFormData({ ...formData, bearer_token: e.target.value })}
            placeholder="Your Toast API token"
          />
        </div>

        <div className="button-group">
          <button className="btn-primary" onClick={handleSave}>
            {editingId ? '💾 Update' : '➕ Save Location'}
          </button>
          {editingId && (
            <button className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <h3>📍 Saved Locations ({locations.length})</h3>

        {loading && <p className="loading">Loading locations...</p>}

        {locations.length === 0 && !loading && (
          <p className="empty">No saved locations yet. Add one above!</p>
        )}

        {locations.map(location => (
          <div key={location.id} className="location-item">
            <div className="location-info">
              <div className="location-name">{location.name}</div>
              <div className="location-detail">Code: {location.location_code || '—'}</div>
              <div className="location-detail">ID: {location.restaurant_id.substring(0, 8)}...</div>
              <div className="location-time">Saved: {new Date(location.created_at).toLocaleDateString()}</div>
            </div>
            <div className="location-actions">
              <button
                className="btn-edit"
                onClick={() => handleEdit(location)}
                title="Edit"
              >
                ✏️
              </button>
              <button
                className="btn-delete"
                onClick={() => handleDelete(location.id)}
                title="Delete"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
